var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useState } from 'react';
import { ActionStrategyId } from '@daredrop-com/rewards-api-contract';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { giveawayEndpointIds } from 'root/src/shared/giveaways/giveaways.constants';
import { authApiDomain } from 'root/src/shared/constants/authApi';
import { apiGatewayEndpoint } from 'root/src/shared/constants/aws';
import { routeUrls } from 'root/src/shared/descriptions/routes/routeUrls';
import { getCookie } from 'root/src/client/v2/browser/helpers/cookies';
import { useDareDropApi } from 'root/src/client/v2/common/hooks/useDareDropApi';
import { AuthBroadcastEvent, getAuthBroadcastChannel, } from 'root/src/client/web/auth/fixtures/auth.fixtures';
const openSteamPopup = (event) => {
    const width = 540;
    const height = 700;
    const left = event.screenX - (width / 2);
    const top = event.screenY - height;
    const popup = window.open('about:blank', 'steam-auth', `width=${width},height=${height},left=${left},top=${top}`);
    if (!popup) {
        throw new Error('Could not open popup, please check your browser settings');
    }
    return popup;
};
const submitSteamEntry = (popup, params) => {
    const url = new URL(`https://${authApiDomain}/sign-in/steam`);
    const state = encodeURIComponent(JSON.stringify({
        actionId: params.actionId,
        giveawayId: params.giveawayId,
        redirectURL: `${apiGatewayEndpoint}${giveawayEndpointIds.CREATE_GIVEAWAY_ENTRY_CALLBACK}`,
        fallbackURL: `${window.location.origin}${routeUrls.authPopupCallback()}`,
        token: getCookie('accessToken'),
        popup: true,
        soft: true,
    }));
    url.searchParams.set('state', state);
    popup.location.href = url.toString();
    const broadcastChannel = getAuthBroadcastChannel();
    return new Promise((resolve, reject) => {
        const checkedClose = setInterval(() => {
            if (popup.closed) {
                cleanup();
                reject(new Error('Steam sign-in popup closed'));
            }
        }, 1000);
        const cleanup = () => {
            broadcastChannel.close();
            clearInterval(checkedClose);
            if (!popup.closed) {
                popup.close();
            }
        };
        broadcastChannel.addEventListener('message', (messageEvent) => {
            var _a;
            const { type } = messageEvent.data;
            if (type === AuthBroadcastEvent.AUTH_SUCCESS) {
                cleanup();
                resolve();
                return;
            }
            if (type === AuthBroadcastEvent.AUTH_ERROR) {
                cleanup();
                const error = new Error((_a = messageEvent.data.message) !== null && _a !== void 0 ? _a : 'Steam verification failed');
                if (messageEvent.data.code) {
                    error.code = messageEvent.data.code;
                }
                reject(error);
                return;
            }
            cleanup();
            reject(new Error('Unknown message type'));
        });
        broadcastChannel.addEventListener('messageerror', () => {
            cleanup();
            reject(new Error('Steam sign-in message error'));
        });
    });
};
export const useCreatorGiveawayEntry = ({ giveaway, creatorUserId }) => {
    const { fetcher } = useDareDropApi();
    const utils = trpc.useUtils();
    const [isSteamLoading, setIsSteamLoading] = useState(false);
    // Refetch the creator's giveaways after any entry so the CTA reflects the new state (the active
    // giveaway's viewer fields: isEntered / isSteamEntered / isSMSEntered).
    const refetchGiveaways = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        yield utils.giveaways.getUserGiveaways.invalidate();
    }), [utils]);
    const saveTerms = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        yield fetcher({ endpointId: giveawayEndpointIds.SAVE_GIVEAWAY_TERMS }, true);
    }), [fetcher]);
    // The plain enter action (already verified via Steam / SMS this giveaway).
    const enterGiveaway = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!giveaway) {
            throw new Error('Giveaway not found, refresh the page and try again.');
        }
        yield saveTerms();
        yield fetcher({
            endpointId: giveawayEndpointIds.ENTER_GIVEAWAY,
            payload: { giveawayId: giveaway.id },
        }, true);
        yield refetchGiveaways();
    }), [giveaway, saveTerms, fetcher, refetchGiveaways]);
    // SMS path: submit the verify-SMS action entry, then enter.
    const enterViaSms = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!giveaway) {
            throw new Error('Giveaway not found, refresh the page and try again.');
        }
        const smsAction = giveaway.actions.find(action => action.strategyId === ActionStrategyId.EnterGiveawayVerifySms);
        if (!smsAction) {
            throw new Error('SMS verification was not configured for this giveaway.');
        }
        yield saveTerms();
        yield fetcher({
            endpointId: giveawayEndpointIds.CREATE_GIVEAWAY_ENTRY,
            payload: { actionId: smsAction.id, giveawayId: giveaway.id },
        }, true);
        yield fetcher({
            endpointId: giveawayEndpointIds.ENTER_GIVEAWAY,
            payload: { giveawayId: giveaway.id },
        }, true);
        yield refetchGiveaways();
    }), [giveaway, saveTerms, fetcher, refetchGiveaways]);
    // Steam path: open the popup (synchronously, to keep the user gesture), submit the entry, then enter.
    const enterViaSteam = useCallback((event) => __awaiter(void 0, void 0, void 0, function* () {
        if (!giveaway) {
            throw new Error('Giveaway not found, refresh the page and try again.');
        }
        const steamAction = giveaway.actions.find(action => action.strategyId === ActionStrategyId.EnterGiveawaySteam);
        if (!steamAction) {
            throw new Error('Steam verification was not configured for this giveaway.');
        }
        const popup = openSteamPopup(event);
        setIsSteamLoading(true);
        try {
            yield submitSteamEntry(popup, { actionId: steamAction.id, giveawayId: giveaway.id });
            yield saveTerms();
            yield fetcher({
                endpointId: giveawayEndpointIds.ENTER_GIVEAWAY,
                payload: { giveawayId: giveaway.id },
            }, true);
            yield refetchGiveaways();
        }
        finally {
            setIsSteamLoading(false);
        }
    }), [giveaway, saveTerms, fetcher, refetchGiveaways]);
    const isEntered = Boolean(giveaway === null || giveaway === void 0 ? void 0 : giveaway.isEntered);
    const isVerified = Boolean((giveaway === null || giveaway === void 0 ? void 0 : giveaway.isSteamEntered) || (giveaway === null || giveaway === void 0 ? void 0 : giveaway.isSMSEntered));
    const isOwnGiveaway = Boolean(creatorUserId) && getCookie('userId') === creatorUserId;
    return {
        enterGiveaway,
        enterViaSms,
        enterViaSteam,
        saveTerms,
        refetchGiveaways,
        isSteamLoading,
        isEntered,
        isVerified,
        isOwnGiveaway,
    };
};
